import { HydraUIAPIError } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';

export type SsoIdentityProvider = 'google_oidc' | 'okta_oidc' | 'azure_oidc' | 'generic_oidc' | 'generic_saml';

/**
 * The subset of an account's SSO configuration returned by the unauthenticated
 * `login_sso_info` endpoint — only what the login flow needs to drive the SSO
 * step. (Not the full account-admin SSO configuration record.)
 */
export type SsoConfiguration = {
  identity_provider: SsoIdentityProvider;
  oidc_google_hosted_domain?: string | null;
  oidc_client_id?: string | null;
  oidc_issuer_url?: string | null;
  saml_issuer_url?: string | null;
};

export type SsoInfo = {
  ssoConfiguration: SsoConfiguration | null;
  /** The Google account email on record for this user, used as `login_hint`. */
  userExternalIdentityEmail: string | null;
};

/**
 * Look up the SSO configuration for an email at login time.
 *
 * Hits the unauthenticated `login_sso_info` endpoint. A 404 means the account
 * has no SSO configured (the common case), which resolves to a null
 * configuration rather than throwing.
 */
export async function fetchSsoInfo({ email, addAccount }: { email: string; addAccount: boolean }): Promise<SsoInfo> {
  try {
    const data = await hydraUiApi.auth.getLoginSsoInfo({
      params: { query: { email, add_session: String(addAccount) } },
    });

    return {
      ssoConfiguration: {
        ...data.sso_configuration,
        identity_provider: data.sso_configuration.identity_provider as SsoIdentityProvider,
      },
      userExternalIdentityEmail: data.user_external_identity_email ?? null,
    };
  } catch (error) {
    if (error instanceof HydraUIAPIError && error.status === 404) {
      return { ssoConfiguration: null, userExternalIdentityEmail: null };
    }
    throw error;
  }
}
